// Range selection helpers — map distance/time domains to sample indices.

import { hasValidDistance } from "./chartAxis";
import { distanceUnit, distanceValue, METERS_PER_MILE } from "./format";

export function getActivityTimeOrigin(samples, activityStartTime) {
  if (activityStartTime) {
    const ms = new Date(activityStartTime).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  const first = samples[0]?.t;
  if (!first) return null;
  const ms = new Date(first).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function sampleElapsedSeconds(sample, originMs, fallbackIndex = 0) {
  if (sample?.t && originMs != null) {
    const ms = new Date(sample.t).getTime();
    if (Number.isFinite(ms)) return Math.max(0, (ms - originMs) / 1000);
  }
  return fallbackIndex;
}

export function getRangeBounds(samples, activityStartTime) {
  const hasDistance = hasValidDistance(samples);
  const originMs = getActivityTimeOrigin(samples, activityStartTime);
  const lastIdx = Math.max(samples.length - 1, 0);
  const startDistM = samples[0]?.distance_m ?? 0;
  const endDistM = samples[lastIdx]?.distance_m ?? startDistM;
  const totalDistanceM = hasDistance ? Math.max(0, endDistM - startDistM) : null;
  const totalDurationS =
    samples.length > 0
      ? sampleElapsedSeconds(samples[lastIdx], originMs, lastIdx)
      : 0;

  return {
    hasDistance,
    originMs,
    totalDistanceM,
    totalDistanceKm: totalDistanceM != null ? totalDistanceM / 1000 : null,
    totalDurationS,
    totalSamples: samples.length,
  };
}

export function indicesForDistanceRange(samples, startM, endM) {
  if (!samples.length) return { startIndex: 0, endIndex: 0 };

  const baseM = samples[0]?.distance_m ?? 0;
  const absStart = baseM + Math.min(startM, endM);
  const absEnd = baseM + Math.max(startM, endM);

  let startIndex = 0;
  let endIndex = samples.length - 1;

  for (let i = 0; i < samples.length; i += 1) {
    if ((samples[i].distance_m ?? 0) >= absStart) {
      startIndex = i;
      break;
    }
  }
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    if ((samples[i].distance_m ?? 0) <= absEnd) {
      endIndex = i;
      break;
    }
  }

  if (endIndex < startIndex) endIndex = startIndex;
  return { startIndex, endIndex };
}

export function indicesForTimeRange(samples, startSec, endSec, originMs) {
  if (!samples.length) return { startIndex: 0, endIndex: 0 };

  const lo = Math.min(startSec, endSec);
  const hi = Math.max(startSec, endSec);

  let startIndex = 0;
  let endIndex = samples.length - 1;

  for (let i = 0; i < samples.length; i += 1) {
    if (sampleElapsedSeconds(samples[i], originMs, i) >= lo) {
      startIndex = i;
      break;
    }
  }
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    if (sampleElapsedSeconds(samples[i], originMs, i) <= hi) {
      endIndex = i;
      break;
    }
  }

  if (endIndex < startIndex) endIndex = startIndex;
  return { startIndex, endIndex };
}

export function distanceRangeFromIndices(samples, startIndex, endIndex, unitSystem = "metric") {
  const baseM = samples[0]?.distance_m ?? 0;
  const d0 = samples[startIndex]?.distance_m ?? baseM;
  const d1 = samples[endIndex]?.distance_m ?? d0;
  const startM = Math.max(0, d0 - baseM);
  const endM = Math.max(0, d1 - baseM);
  return {
    startM,
    endM,
    startKm: distanceValue(startM, unitSystem),
    endKm: distanceValue(endM, unitSystem),
  };
}

export function timeRangeFromIndices(samples, startIndex, endIndex, originMs) {
  const startSec = sampleElapsedSeconds(samples[startIndex], originMs, startIndex);
  const endSec = sampleElapsedSeconds(samples[endIndex], originMs, endIndex);
  return { startSec, endSec };
}

export function domainValuesFromIndices(
  mode,
  samples,
  startIndex,
  endIndex,
  bounds,
  unitSystem = "metric"
) {
  if (mode === "distance" && bounds.hasDistance) {
    const { startKm, endKm } = distanceRangeFromIndices(
      samples,
      startIndex,
      endIndex,
      unitSystem
    );
    return [startKm, endKm];
  }
  const { startSec, endSec } = timeRangeFromIndices(
    samples,
    startIndex,
    endIndex,
    bounds.originMs
  );
  return [startSec, endSec];
}

export function indicesFromDomainValues(
  mode,
  samples,
  startVal,
  endVal,
  bounds,
  unitSystem = "metric"
) {
  if (mode === "distance" && bounds.hasDistance) {
    const metersPerUnit = unitSystem === "imperial" ? METERS_PER_MILE : 1000;
    return indicesForDistanceRange(samples, startVal * metersPerUnit, endVal * metersPerUnit);
  }
  return indicesForTimeRange(samples, startVal, endVal, bounds.originMs);
}

export function isFullSelection(startIndex, endIndex, totalSamples) {
  if (!totalSamples || totalSamples <= 1) return true;
  return startIndex <= 0 && endIndex >= totalSamples - 1;
}

export function formatDurationHms(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function parseTimeInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);

  const parts = raw.split(":").map((p) => Number(p.trim()));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

export function parseDistanceInput(value, unitSystem = "metric") {
  const unit = distanceUnit(unitSystem);
  const raw = String(value ?? "").trim().replace(new RegExp(unit, "i"), "").trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatRangeSummary(
  mode,
  samples,
  startIndex,
  endIndex,
  bounds,
  unitSystem = "metric"
) {
  if (mode === "distance" && bounds.hasDistance) {
    const { startKm, endKm } = distanceRangeFromIndices(
      samples,
      startIndex,
      endIndex,
      unitSystem
    );
    const distKm = Math.max(0, endKm - startKm);
    const unit = distanceUnit(unitSystem);
    return {
      primary: `Selected distance: ${startKm.toFixed(2)}–${endKm.toFixed(2)} ${unit}`,
      secondary: `Span: ${distKm.toFixed(2)} ${unit}`,
    };
  }

  const { startSec, endSec } = timeRangeFromIndices(
    samples,
    startIndex,
    endIndex,
    bounds.originMs
  );
  const duration = Math.max(0, endSec - startSec);
  return {
    primary: `Selected activity time: ${formatDurationHms(startSec)}–${formatDurationHms(endSec)}`,
    secondary: `Selected duration: ${formatDurationHms(duration)}`,
  };
}

export function sliceForIndices(samples, startIndex, endIndex) {
  const startIdx = Math.max(0, Math.min(startIndex, samples.length - 1));
  const endIdx = Math.max(startIdx, Math.min(endIndex, samples.length - 1));
  const startM = samples[startIdx]?.distance_m ?? 0;
  const endM = samples[endIdx]?.distance_m ?? startM;
  return { startIdx, endIdx, startM, endM };
}
